import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

function fmtDateBR(iso) {
    const value = String(iso || "").slice(0, 10);

    if (!value) return "-";

    const [year, month, day] = value.split("-");

    if (!year || !month || !day) {
        return value;
    }

    return `${day}/${month}/${year}`;
}

function statusLabel(status) {
    const normalizedStatus = String(
        status || ""
    ).toUpperCase();

    const labels = {
        OPEN: "ABERTA",
        PENDING: "PENDENTE",
        ATFACTORY: "NA FÁBRICA",
        SENDTOFACTORY: "ENVIAR PARA FÁBRICA",
        RETURNSELLER: "DEVOLVER PARA REVENDEDORA",
        SOLVED: "RESOLVIDO",
        CANCELED: "ANULADO",
    };

    return (
        labels[normalizedStatus] ||
        normalizedStatus ||
        "-"
    );
}

function getWarrantyDisplayData(warranty) {
    const resolvedProduct =
        warranty?.resolved_product ?? null;

    const snapshot =
        warranty?.product_snapshot ??
        warranty?.kit_product_snapshot ??
        {};

    /*
     * Ordem de prioridade:
     *
     * 1. Campos resolvidos pelo backend a partir das FKs reais;
     * 2. resolved_product;
     * 3. campos históricos da garantia;
     * 4. snapshot antigo apenas como último fallback.
     */

    const sellerId =
        warranty?.seller_id ??
        resolvedProduct?.seller_id ??
        resolvedProduct?.kit__seller_id ??
        (
            typeof warranty?.seller === "number"
                ? warranty.seller
                : warranty?.seller?.id
        ) ??
        snapshot?.seller_id ??
        snapshot?.kit__seller_id ??
        null;

    const sellerName =
        warranty?.seller_nome ??
        warranty?.kit_seller_nome ??
        resolvedProduct?.seller_nome ??
        resolvedProduct?.kit__seller__nome ??
        warranty?.seller?.nome ??
        snapshot?.seller_nome ??
        snapshot?.kit__seller__nome ??
        "-";

    const kitProductId =
        warranty?.kit_product_id ??
        resolvedProduct?.kit_product_pk ??
        resolvedProduct?.pk ??
        (
            typeof warranty?.kit_product === "number"
                ? warranty.kit_product
                : warranty?.kit_product?.id ??
                warranty?.kit_product?.pk
        ) ??
        snapshot?.kit_product_pk ??
        snapshot?.pk ??
        null;

    const productCode =
        warranty?.product_code_real ??
        resolvedProduct?.product_id_produto ??
        warranty?.product_code ??
        warranty?.product_id_produto ??
        snapshot?.product_id_produto ??
        "-";

    const productModel =
        warranty?.product_model ??
        resolvedProduct?.content_type__model ??
        warranty?.content_type__model ??
        snapshot?.content_type__model ??
        "-";

    const kitNumber =
        warranty?.kit_number ??
        resolvedProduct?.kit__kit_number ??
        warranty?.kit__kit_number ??
        snapshot?.kit__kit_number ??
        "-";

    const imageUrl =
        warranty?.product_image_url ??
        resolvedProduct?.imageUrl ??
        warranty?.imageUrl ??
        warranty?.image_url ??
        snapshot?.imageUrl ??
        snapshot?.image_url ??
        "";

    return {
        sellerId,
        sellerName,
        kitProductId,
        productCode,
        productModel,
        kitNumber,
        imageUrl,
    };
}

export default function WarrantyListTable({
    rows,
    loading,
    error,
    subtleBorder,
    softText,
    onEdit,
}) {
    const [imgOpen, setImgOpen] = useState(false);
    const [activeImg, setActiveImg] = useState("");
    const [activeTitle, setActiveTitle] = useState("");

    const topScrollRef = useRef(null);
    const topScrollInnerRef = useRef(null);
    const tableContainerRef = useRef(null);
    const syncingRef = useRef(false);

    const safeRows = useMemo(() => {
        return Array.isArray(rows) ? rows : [];
    }, [rows]);

    const openImage = (url, title) => {
        if (!url) return;

        setActiveImg(url);
        setActiveTitle(title || "");
        setImgOpen(true);
    };

    const closeImage = () => {
        setImgOpen(false);
        setActiveImg("");
        setActiveTitle("");
    };

    useEffect(() => {
        const topElement = topScrollRef.current;
        const topInnerElement =
            topScrollInnerRef.current;
        const tableElement =
            tableContainerRef.current;

        if (
            !topElement ||
            !topInnerElement ||
            !tableElement
        ) {
            return undefined;
        }

        const syncWidths = () => {
            topInnerElement.style.width =
                `${tableElement.scrollWidth}px`;
        };

        const handleTopScroll = () => {
            if (syncingRef.current) return;

            syncingRef.current = true;
            tableElement.scrollLeft =
                topElement.scrollLeft;

            requestAnimationFrame(() => {
                syncingRef.current = false;
            });
        };

        const handleBottomScroll = () => {
            if (syncingRef.current) return;

            syncingRef.current = true;
            topElement.scrollLeft =
                tableElement.scrollLeft;

            requestAnimationFrame(() => {
                syncingRef.current = false;
            });
        };

        syncWidths();

        topElement.addEventListener(
            "scroll",
            handleTopScroll
        );

        tableElement.addEventListener(
            "scroll",
            handleBottomScroll
        );

        window.addEventListener(
            "resize",
            syncWidths
        );

        let resizeObserver;

        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(
                syncWidths
            );

            resizeObserver.observe(tableElement);
        }

        return () => {
            topElement.removeEventListener(
                "scroll",
                handleTopScroll
            );

            tableElement.removeEventListener(
                "scroll",
                handleBottomScroll
            );

            window.removeEventListener(
                "resize",
                syncWidths
            );

            resizeObserver?.disconnect();
        };
    }, [safeRows, loading]);

    return (
        <Box sx={{ mt: 3 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    mb: 1,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight={900}
                >
                    Garantias
                </Typography>

                {loading && (
                    <Stack
                        spacing={0.75}
                        sx={{ minWidth: 220 }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            <Chip
                                size="small"
                                label="Atualizando"
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 800,
                                }}
                            />

                            <CircularProgress size={16} />

                            <Typography
                                variant="body2"
                                color={softText}
                                sx={{ fontWeight: 700 }}
                            >
                                Carregando lista…
                            </Typography>
                        </Stack>

                        <Box
                            sx={{
                                height: 6,
                                borderRadius: 999,
                                overflow: "hidden",
                                bgcolor:
                                    "rgba(0,0,0,0.08)",
                            }}
                        >
                            <Box
                                sx={{
                                    height: "100%",
                                    width: "35%",
                                    borderRadius: 999,
                                    bgcolor:
                                        "success.main",
                                    animation:
                                        "wprog 1.05s ease-in-out infinite",
                                    "@keyframes wprog": {
                                        "0%": {
                                            transform:
                                                "translateX(-120%)",
                                        },
                                        "100%": {
                                            transform:
                                                "translateX(320%)",
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Stack>
                )}
            </Stack>

            {error && (
                <Typography
                    variant="body2"
                    color="error"
                    sx={{ mb: 1 }}
                >
                    {error}
                </Typography>
            )}

            {!loading && safeRows.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        border: subtleBorder,
                    }}
                >
                    <Typography
                        variant="body2"
                        color={softText}
                    >
                        Nenhuma garantia encontrada.
                    </Typography>
                </Paper>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        border: subtleBorder,
                        overflow: "hidden",
                    }}
                >
                    {/* Scroll horizontal superior */}
                    <Box
                        ref={topScrollRef}
                        sx={{
                            overflowX: "auto",
                            overflowY: "hidden",
                            height: 14,
                            borderBottom:
                                "1px solid rgba(0,0,0,0.12)",
                            bgcolor:
                                "rgba(0,0,0,0.02)",
                            "&::-webkit-scrollbar": {
                                height: 10,
                            },
                        }}
                    >
                        <Box
                            ref={topScrollInnerRef}
                            sx={{ height: 1 }}
                        />
                    </Box>

                    <TableContainer
                        ref={tableContainerRef}
                        sx={{
                            maxHeight: {
                                xs: 420,
                                md: 560,
                            },
                            overflow: "auto",
                            "&::-webkit-scrollbar": {
                                width: 10,
                                height: 10,
                            },
                        }}
                    >
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                minWidth: 1400,
                                tableLayout: "auto",
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            ...headerCellSx,
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 4,
                                            minWidth: 90,
                                            borderRight: "1px solid rgba(0,0,0,0.10)",
                                        }}
                                    >
                                        Editar
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Nº
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Recebimento
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Imagem
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Status
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Vendedora
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Modelo
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Produto
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        KitProduct ID
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Lote
                                    </TableCell>

                                    <TableCell sx={headerCellSx}>
                                        Contato
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            ...headerCellSx,
                                            minWidth: 280,
                                        }}
                                    >
                                        Problema
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            ...headerCellSx,
                                            minWidth: 240,
                                        }}
                                    >
                                        Solução
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {safeRows.map(
                                    (warranty, index) => {
                                        const warrantyId =
                                            warranty?.id ??
                                            warranty?.pk ??
                                            `${warranty?.number_ref ?? "warranty"}-${index}`;

                                        const {
                                            sellerId,
                                            sellerName,
                                            kitProductId,
                                            productCode,
                                            productModel,
                                            kitNumber,
                                            imageUrl,
                                        } =
                                            getWarrantyDisplayData(
                                                warranty
                                            );

                                        const imageTitle =
                                            `Produto ${productCode}` +
                                            ` • Kit #${kitNumber}`;

                                        const solution =
                                            warranty?.observacao ??
                                            warranty?.solution ??
                                            warranty?.notes ??
                                            "-";

                                        const numberLabel =
                                            warranty?.number_ref ??
                                            "-";

                                        return (
                                            <TableRow
                                                key={String(warrantyId)}
                                                hover
                                                sx={{
                                                    cursor: "default",
                                                    "& td": {
                                                        verticalAlign: "middle",
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 2,
                                                        minWidth: 90,
                                                        bgcolor: "background.paper",
                                                        borderRight: "1px solid rgba(0,0,0,0.10)",
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => onEdit?.(warranty)}
                                                    >
                                                        Editar
                                                    </Button>
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    <Chip
                                                        size="small"
                                                        label={numberLabel}
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {fmtDateBR(warranty?.received_at)}
                                                </TableCell>


                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {imageUrl ? (
                                                        <Box
                                                            role="button"
                                                            tabIndex={
                                                                0
                                                            }
                                                            onClick={() =>
                                                                openImage(
                                                                    imageUrl,
                                                                    imageTitle
                                                                )
                                                            }
                                                            onKeyDown={(
                                                                event
                                                            ) => {
                                                                if (
                                                                    event.key ===
                                                                    "Enter" ||
                                                                    event.key ===
                                                                    " "
                                                                ) {
                                                                    event.preventDefault();

                                                                    openImage(
                                                                        imageUrl,
                                                                        imageTitle
                                                                    );
                                                                }
                                                            }}
                                                            title="Abrir imagem"
                                                            sx={{
                                                                width: 44,
                                                                height: 44,
                                                                borderRadius: 2,
                                                                overflow:
                                                                    "hidden",
                                                                border:
                                                                    "1px solid rgba(0,0,0,0.12)",
                                                                cursor:
                                                                    "pointer",
                                                                display:
                                                                    "inline-block",
                                                                backgroundColor:
                                                                    "rgba(0,0,0,0.04)",
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={
                                                                    imageUrl
                                                                }
                                                                alt={
                                                                    imageTitle
                                                                }
                                                                loading="lazy"
                                                                referrerPolicy="no-referrer"
                                                                sx={{
                                                                    width:
                                                                        "100%",
                                                                    height:
                                                                        "100%",
                                                                    objectFit:
                                                                        "cover",
                                                                    display:
                                                                        "block",
                                                                }}
                                                                onError={(
                                                                    event
                                                                ) => {
                                                                    event.currentTarget.style.display =
                                                                        "none";
                                                                }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                opacity: 0.7,
                                                            }}
                                                        >
                                                            -
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={statusLabel(
                                                            warranty?.status
                                                        )}
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                    title={
                                                        sellerId != null
                                                            ? `ID: ${sellerId}`
                                                            : ""
                                                    }
                                                >
                                                    {sellerName}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {productModel}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {productCode}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {kitProductId ??
                                                        "-"}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {warranty?.lot_code ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        minWidth: 180,
                                                        maxWidth: 240,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={
                                                            warranty?.customer_contact ||
                                                            ""
                                                        }
                                                    >
                                                        {warranty?.customer_contact ||
                                                            "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        minWidth: 280,
                                                        maxWidth: 360,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={
                                                            warranty?.problem_reported ||
                                                            ""
                                                        }
                                                    >
                                                        {warranty?.problem_reported ||
                                                            "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        minWidth: 240,
                                                        maxWidth: 300,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={
                                                            solution ===
                                                                "-"
                                                                ? ""
                                                                : String(
                                                                    solution
                                                                )
                                                        }
                                                    >
                                                        {solution}
                                                    </Typography>
                                                </TableCell>


                                            </TableRow>
                                        );
                                    }
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog
                open={imgOpen}
                onClose={closeImage}
                fullScreen
                PaperProps={{
                    sx: {
                        bgcolor:
                            "rgba(0,0,0,0.95)",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <IconButton
                        onClick={closeImage}
                        aria-label="Fechar imagem"
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            color: "white",
                            bgcolor:
                                "rgba(255,255,255,0.12)",
                            "&:hover": {
                                bgcolor:
                                    "rgba(255,255,255,0.18)",
                            },
                        }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>

                    {!!activeTitle && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 14,
                                left: 14,
                                zIndex: 2,
                                color: "white",
                                bgcolor:
                                    "rgba(0,0,0,0.35)",
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 2,
                                maxWidth: "75%",
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={800}
                                noWrap
                                title={activeTitle}
                            >
                                {activeTitle}
                            </Typography>
                        </Box>
                    )}

                    {activeImg && (
                        <Box
                            component="img"
                            src={activeImg}
                            alt={
                                activeTitle ||
                                "Imagem do produto"
                            }
                            referrerPolicy="no-referrer"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    )}
                </Box>
            </Dialog>
        </Box>
    );
}

const headerCellSx = {
    fontWeight: 800,
    whiteSpace: "nowrap",
    bgcolor: "background.paper",
};